import { useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { IconSparkle } from "@/components/Icons";
import { Loader2, GitBranch, Camera, Pencil, Box, Upload, X } from "lucide-react";
import { upgradeFrameStyle, regenerateFrame } from "@/services/shotService";
import { toast } from "sonner";
import EquipmentTab from "./EquipmentTab";

const TABS = [
  { id: "prompt", label: "Prompt", icon: Pencil },
  { id: "equipment", label: "Equipment", icon: Camera },
  { id: "reference", label: "Reference", icon: Upload },
  { id: "blocking", label: "Blocking", icon: Box },
];

export default function ShotRedesignPanel({ frame, concept, onRegenerate, onVersions, onUpdate }) {
  const isConceptMode = !!concept;
  const entity = concept || frame;
  const settings = concept?.settings || {};

  const [activeTab, setActiveTab] = useState("prompt");
  const [editPrompt, setEditPrompt] = useState(
    isConceptMode ? (concept?.current_prompt || "") : (frame?.prompt || "")
  );
  const [regenerating, setRegenerating] = useState(false);

  // Equipment state — initialized from frame or concept settings
  const [shotType, setShotType] = useState(isConceptMode ? (settings.shot_type || "medium") : (frame?.shot_type || "medium"));
  const [cameraAngle, setCameraAngle] = useState(isConceptMode ? (settings.camera_angle || "eye-level") : (frame?.camera_angle || "eye-level"));
  const [cameraMovement, setCameraMovement] = useState(isConceptMode ? (settings.camera_movement || "static") : (frame?.camera_movement || "static"));
  const [lighting, setLighting] = useState(isConceptMode ? (settings.lighting || "natural") : (frame?.lighting || "natural"));
  const [lens, setLens] = useState(isConceptMode ? (settings.lens || "50mm") : (frame?.lens || "50mm"));

  // Reference state
  const [referenceImage, setReferenceImage] = useState(null);
  const [referencePreview, setReferencePreview] = useState(null);
  const fileInputRef = useRef(null);

  // QuickDraw lazy loaded
  const [showQuickDraw, setShowQuickDraw] = useState(false);
  const [QuickDrawComponent, setQuickDrawComponent] = useState(null);

  // Blocking lazy loaded
  const [showBlocking, setShowBlocking] = useState(false);

  const handleRegenerate = useCallback(async (opts = {}) => {
    if (!editPrompt.trim()) return;
    setRegenerating(true);
    try {
      const entityId = isConceptMode ? concept.concept_id : frame.id;
      const equipment = {
        shot_type: shotType,
        camera_angle: cameraAngle,
        camera_movement: cameraMovement,
        lighting,
        lens,
        reference_image: opts.reference_image || referenceImage || undefined,
      };
      await onRegenerate(entityId, editPrompt, equipment);
    } finally {
      setRegenerating(false);
    }
  }, [isConceptMode, concept?.concept_id, frame?.id, editPrompt, shotType, cameraAngle, cameraMovement, lighting, lens, referenceImage, onRegenerate]);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(",")[1]; // strip data:image/...;base64,
      setReferenceImage(base64);
      setReferencePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const loadQuickDraw = async () => {
    if (!QuickDrawComponent) {
      const mod = await import("@/components/EmbeddedQuickDraw");
      setQuickDrawComponent(() => mod.default);
    }
    setShowQuickDraw(true);
  };

  if (!frame && !concept) return null;

  return (
    <div className="w-80 bg-[var(--bg-surface)] rounded-2xl flex flex-col border border-[var(--border-subtle)] flex-shrink-0 overflow-hidden">
      {/* Header */}
      <div className="px-5 pt-5 pb-3">
        {isConceptMode ? (
          <>
            <p className="text-xs text-[var(--text-muted)] mb-1">{concept.creator_name}'s concept</p>
            <p className="text-sm font-semibold text-[var(--text-primary)] truncate">{concept.name}</p>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-2">
              <span className="chip chip-gold">Scene {frame.scene_number}</span>
              <span className="chip">Shot {frame.frame_number}</span>
            </div>
            {frame.shot_type && (
              <p className="text-sm font-semibold text-[var(--text-primary)] capitalize">
                {frame.shot_type.replace(/-/g, " ")}
              </p>
            )}
          </>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-[var(--border-subtle)] px-2">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1 py-2 text-[10px] font-medium transition-colors border-b-2 ${
              activeTab === tab.id
                ? "text-[var(--color-gold-400)] border-[var(--color-gold-500)]"
                : "text-[var(--text-muted)] border-transparent hover:text-[var(--text-primary)]"
            }`}
          >
            <tab.icon size={12} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-5">
        {activeTab === "prompt" && (
          <div className="space-y-3">
            <div>
              <label className="label mb-2 block">Prompt</label>
              <Textarea
                value={editPrompt}
                onChange={(e) => setEditPrompt(e.target.value)}
                className="input resize-none h-28 text-xs"
                placeholder="Describe the shot..."
              />
            </div>

            <Button
              onClick={() => handleRegenerate()}
              disabled={regenerating || !editPrompt.trim()}
              className="btn-primary w-full"
            >
              {regenerating ? <Loader2 size={16} className="animate-spin" /> : <IconSparkle size={16} />}
              <span className="ml-2">Regenerate</span>
            </Button>

            {onVersions && (
              <Button onClick={onVersions} className="btn-secondary w-full">
                <GitBranch size={14} />
                <span className="ml-2">Versions</span>
              </Button>
            )}

            {/* Style Ladder — only for frame mode */}
            {!isConceptMode && frame && (
              <div className="pt-3 border-t border-[var(--border-subtle)]">
                <label className="label mb-2 block">Style Tier</label>
                <div className="flex gap-1.5">
                  {["sketch", "concept", "previs"].map((tier) => (
                    <button
                      key={tier}
                      onClick={async () => {
                        if (frame.stage !== tier && frame.status === "completed") {
                          try {
                            await upgradeFrameStyle(frame.id, tier);
                            toast.success(`Upgrading to ${tier}...`);
                            if (onUpdate) onUpdate();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Upgrade failed");
                          }
                        }
                      }}
                      disabled={frame.stage === tier || frame.status !== "completed"}
                      className={`flex-1 py-1.5 rounded-lg text-[10px] font-medium transition-all ${
                        frame.stage === tier
                          ? "bg-[var(--color-gold-500)]/20 text-[var(--color-gold-400)] border border-[var(--color-gold-500)]/30"
                          : "bg-[var(--bg-overlay)] text-[var(--text-muted)] hover:text-[var(--text-primary)] border border-transparent"
                      }`}
                    >
                      {tier === "sketch" ? "\u270F\uFE0F" : tier === "concept" ? "\uD83C\uDFA8" : "\uD83D\uDCF7"} {tier.charAt(0).toUpperCase() + tier.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "equipment" && (
          <div className="space-y-4">
            <EquipmentTab
              shotType={shotType} setShotType={setShotType}
              cameraAngle={cameraAngle} setCameraAngle={setCameraAngle}
              cameraMovement={cameraMovement} setCameraMovement={setCameraMovement}
              lighting={lighting} setLighting={setLighting}
              lens={lens} setLens={setLens}
            />
            <Button
              onClick={() => handleRegenerate()}
              disabled={regenerating || !editPrompt.trim()}
              className="btn-primary w-full"
            >
              {regenerating ? <Loader2 size={16} className="animate-spin" /> : <IconSparkle size={16} />}
              <span className="ml-2">Apply & Regenerate</span>
            </Button>
            <p className="text-[9px] text-[var(--text-muted)] text-center">
              Equipment settings will be saved on the frame and used in prompt engineering
            </p>
          </div>
        )}

        {activeTab === "reference" && (
          <div className="space-y-3">
            {/* Upload reference */}
            <div>
              <label className="label mb-2 block">Upload Reference</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              {referencePreview ? (
                <div className="relative">
                  <img src={referencePreview} alt="Reference" className="w-full rounded-lg border border-[var(--border-subtle)]" />
                  <button
                    onClick={() => { setReferenceImage(null); setReferencePreview(null); }}
                    className="absolute top-1 right-1 p-0.5 bg-black/50 rounded-full"
                  >
                    <X size={12} className="text-white" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full py-6 border-2 border-dashed border-[var(--border-subtle)] rounded-lg text-[var(--text-muted)] hover:border-[var(--color-gold-500)]/50 hover:text-[var(--text-primary)] transition-colors text-xs"
                >
                  <Upload size={20} className="mx-auto mb-1" />
                  Drop or click to upload
                </button>
              )}
            </div>

            {/* Quick Draw */}
            <div>
              <label className="label mb-2 block">Or Sketch It</label>
              {showQuickDraw && QuickDrawComponent ? (
                <div className="h-56 border border-[var(--border-subtle)] rounded-lg overflow-hidden">
                  <QuickDrawComponent
                    onGenerate={(canvasData) => {
                      // Canvas returns base64 PNG
                      const base64 = canvasData.split(",")[1];
                      setReferenceImage(base64);
                      setReferencePreview(canvasData);
                      setShowQuickDraw(false);
                      toast.success("Sketch captured as reference");
                    }}
                    characters={[]}
                    locations={[]}
                    compact
                  />
                </div>
              ) : (
                <button
                  onClick={loadQuickDraw}
                  className="w-full py-4 border border-[var(--border-subtle)] rounded-lg text-[var(--text-muted)] hover:border-[var(--color-gold-500)]/50 hover:text-[var(--text-primary)] transition-colors text-xs"
                >
                  <Pencil size={16} className="mx-auto mb-1" />
                  Open sketch pad
                </button>
              )}
            </div>

            {referenceImage && (
              <Button
                onClick={() => handleRegenerate({ reference_image: referenceImage })}
                disabled={regenerating || !editPrompt.trim()}
                className="btn-primary w-full"
              >
                {regenerating ? <Loader2 size={16} className="animate-spin" /> : <IconSparkle size={16} />}
                <span className="ml-2">Regenerate with Reference</span>
              </Button>
            )}
          </div>
        )}

        {activeTab === "blocking" && (
          <div className="space-y-3">
            <p className="text-xs text-[var(--text-muted)]">
              Use 3D blocking to position characters and camera, then generate a shot from the viewport.
            </p>
            {showBlocking ? (
              <div className="text-center py-4">
                <Loader2 size={20} className="animate-spin mx-auto mb-2 text-[var(--color-gold-400)]" />
                <p className="text-[10px] text-[var(--text-muted)]">Loading 3D environment...</p>
              </div>
            ) : (
              <button
                onClick={() => {
                  setShowBlocking(true);
                  // Open blocking in a dialog/modal — reuse existing ShotBlockingView
                  toast.info("Opening 3D blocking view...");
                  // For now, open in a new window-style modal
                  // This will be integrated more deeply in Phase 3
                  window.__openBlockingView?.({
                    projectId: frame.project_id,
                    sceneNumber: frame.scene_number,
                    onGenerated: (imageData) => {
                      setReferenceImage(imageData);
                      setReferencePreview(`data:image/png;base64,${imageData}`);
                      setShowBlocking(false);
                      toast.success("Blocking captured — regenerate to apply");
                    },
                  });
                  setShowBlocking(false);
                }}
                className="w-full py-8 border border-[var(--border-subtle)] rounded-lg text-[var(--text-muted)] hover:border-[var(--color-gold-500)]/50 hover:text-[var(--text-primary)] transition-colors"
              >
                <Box size={24} className="mx-auto mb-2" />
                <span className="text-xs">Open 3D Blocking</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
